import { useEffect } from 'react';
import { Phone, Calendar, ChevronDown, ArrowRight, Check } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import type { NycPage } from '@/data/nycPages';
import { siblingGuides } from '@/data/practiceGuides';
import { practiceAreaLabels } from '@/data/practiceAreas';
import { pageDates } from '@/data/pageDates';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';

interface Props {
  page: NycPage;
}

const CATEGORY_BREADCRUMBS: Record<string, { label: string; href: string }> = {
  probate: { label: 'Probate', href: '/probate/' },
  'estate-planning': { label: 'Estate Planning', href: '/estate-planning/' },
  trusts: { label: 'Wills & Trusts', href: '/trusts/' },
  'elder-law': { label: 'Elder Law', href: '/elder-law/' },
  'asset-protection': { label: 'Asset Protection', href: '/asset-protection/' },
  guardianship: { label: 'Guardianship', href: '/guardianship/' },
  'power-of-attorney': { label: 'Power of Attorney', href: '/power-of-attorney/' },
  'family-law': { label: 'Family Law', href: '/family-law/' },
  matrimonial: { label: 'Matrimonial', href: '/matrimonial/' },
  'real-estate': { label: 'Real Estate', href: '/real-estate/' },
  'personal-injury': { label: 'Personal Injury', href: '/personal-injury/' },
  general: { label: 'Resources', href: '/resources/' },
};

function buildSchema(p: NycPage) {
  const canonical = `${SITE}${p.url}`;
  const dates = pageDates(p.url);
  const breadcrumb = CATEGORY_BREADCRUMBS[p.category] ?? CATEGORY_BREADCRUMBS.general;

  const schemas: object[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: p.h1,
      description: p.metaDescription,
      author: { '@id': `${SITE}/#organization` },
      publisher: { '@id': `${SITE}/#organization` },
      datePublished: dates.published,
      dateModified: dates.modified,
      mainEntityOfPage: canonical,
      articleSection: breadcrumb.label,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: breadcrumb.label, item: `${SITE}${breadcrumb.href}` },
        { '@type': 'ListItem', position: 3, name: p.h1, item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      url: canonical,
      name: p.metaTitle,
      description: p.metaDescription,
      isPartOf: { '@id': `${SITE}/#website` },
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.lead', '[data-speakable]'],
      },
    },
  ];

  if (p.faq && p.faq.length) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: p.faq.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  return schemas;
}

function Hero({ p }: { p: NycPage }) {
  const breadcrumb = CATEGORY_BREADCRUMBS[p.category] ?? CATEGORY_BREADCRUMBS.general;
  return (
    <section className="relative bg-navy pt-28 lg:pt-32 pb-20 lg:pb-24 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span
          className="absolute bottom-0 -right-8 font-display text-[10rem] lg:text-[16rem] font-bold text-white leading-none whitespace-nowrap"
          style={{ opacity: 0.03 }}
        >
          NYC
        </span>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
          <a href="/" className="hover:text-gold transition-colors">Home</a>
          <span className="mx-2 text-gold/60">/</span>
          <a href={breadcrumb.href} className="hover:text-gold transition-colors">{breadcrumb.label}</a>
          <span className="mx-2 text-gold/60">/</span>
          <span className="text-white/80">{p.h1.length > 60 ? p.h1.slice(0, 60) + '…' : p.h1}</span>
        </nav>

        <div className="max-w-4xl">
          <div className="section-label mb-5 text-gold">{breadcrumb.label} · New York</div>

          <h1 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.1] mb-7">
            {p.h1}
          </h1>

          <p className="lead font-body text-white/85 text-base lg:text-lg leading-relaxed mb-8 max-w-3xl" data-speakable>
            {p.heroLead}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <a href="/appointment/" className="btn-gold">
              <Calendar className="w-4 h-4" />
              Free Consultation
            </a>
            <a
              href="tel:8885291315"
              className="inline-flex items-center gap-2 border border-gold/40 text-gold font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-gold hover:text-navy"
            >
              <Phone className="w-4 h-4" />
              888-529-1315
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Body({ p }: { p: NycPage }) {
  const { ref, visible } = useScrollReveal(0.05);
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-16 lg:py-24 bg-cream">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {p.sections.map((sec, i) => (
          <article
            key={i}
            className={`mb-12 lg:mb-14 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold leading-tight mb-5">
              {sec.heading}
            </h2>
            <div className="space-y-4">
              {sec.paragraphs.map((para, j) => (
                <p key={j} className="font-body text-navy/80 text-base lg:text-lg leading-relaxed">
                  {para}
                </p>
              ))}
            </div>
          </article>
        ))}

        {p.bullets && p.bullets.length > 0 ? (
          <article className="mb-12 lg:mb-14">
            <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold leading-tight mb-5">
              Key Points
            </h2>
            <ul className="space-y-2.5">
              {p.bullets.slice(0, 10).map((b, k) => (
                <li key={k} className="flex items-start gap-3 font-body text-navy/80 text-base lg:text-lg leading-relaxed">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </article>
        ) : null}
      </div>
    </section>
  );
}

function Faq({ p }: { p: NycPage }) {
  const { ref, visible } = useScrollReveal(0.1);
  if (!p.faq || p.faq.length === 0) return null;
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-16 lg:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10">
          <div className="section-label mb-4">FAQ</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl font-bold leading-tight">
            Common <span className="text-gold italic">Questions</span>
          </h2>
        </div>

        <div className="space-y-3">
          {p.faq.map((f, i) => (
            <details
              key={i}
              className={`group bg-cream border border-gold/20 hover:border-gold/40 transition-all duration-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <summary className="flex items-start justify-between gap-4 p-5 lg:p-6 cursor-pointer list-none">
                <span className="font-display text-navy text-base lg:text-lg font-semibold leading-snug group-open:text-gold transition-colors">
                  {f.question}
                </span>
                <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center border border-navy/20 text-navy/60 group-open:bg-gold group-open:border-gold group-open:text-white group-open:rotate-180 transition-all mt-0.5">
                  <ChevronDown className="w-4 h-4" />
                </span>
              </summary>
              <div className="px-5 lg:px-6 pb-5 lg:pb-6">
                <div className="w-8 h-0.5 bg-gold mb-3" />
                <p className="font-body text-navy/75 text-sm lg:text-base leading-relaxed">{f.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Соседние разборы той же практики. Без этого блока каждый разбор держался
 * на единственной входящей ссылке — со своей страницы практики; поломка одной
 * страницы обесточивала бы сразу десяток.
 */
function SiblingGuides({ p }: { p: NycPage }) {
  const siblings = siblingGuides(p.url, 6);
  if (siblings.length === 0) return null;
  return (
    <section className="py-14 lg:py-16 bg-white border-t border-navy/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="section-label mb-6">Continue Reading</div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {siblings.map((g) => (
            <li key={g.url}>
              <a
                href={g.url}
                className="group flex items-start gap-3 p-5 h-full bg-cream border border-navy/10 hover:border-gold hover:shadow-lg transition-all duration-300"
              >
                <span className="font-body text-navy text-base leading-snug group-hover:text-gold transition-colors">
                  {g.title}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function RelatedPracticeAreas({ p }: { p: NycPage }) {
  if (!p.relatedPracticeAreas?.length) return null;
  return (
    <section className="py-16 lg:py-24 bg-cream border-t border-navy/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10">
          <div className="section-label mb-4">Related Practices</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl font-bold leading-tight">
            How We <span className="text-gold italic">Help With This</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {p.relatedPracticeAreas.slice(0, 4).map((slug) => {
            const info = practiceAreaLabels[slug];
            const href = `/${slug}/`;
            return (
              <a
                key={slug}
                href={href}
                className="group block p-6 bg-white border border-navy/10 hover:border-gold hover:shadow-lg transition-all duration-300"
              >
                <div className="w-10 h-0.5 bg-gold mb-4 transition-all duration-300 group-hover:w-16" />
                <h3 className="font-display text-navy text-lg lg:text-xl font-bold mb-2 group-hover:text-gold transition-colors">
                  {info?.label ?? slug}
                </h3>
                <p className="font-body text-navy/65 text-sm leading-relaxed mb-4">
                  {info?.blurb ?? 'Related practice area at Morgan Legal Group.'}
                </p>
                <span className="inline-flex items-center gap-1 font-body text-xs uppercase tracking-wider text-gold font-semibold">
                  Learn More
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBand({ p }: { p: NycPage }) {
  const breadcrumb = CATEGORY_BREADCRUMBS[p.category] ?? CATEGORY_BREADCRUMBS.general;
  return (
    <section className="bg-gold py-16 lg:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
          Ready to Talk About Your {breadcrumb.label} Matter?
        </h2>
        <p className="font-body text-navy/85 text-base lg:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
          Schedule a free consultation with Morgan Legal Group. A senior attorney will personally review your situation
          and outline next steps.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href="/appointment/" className="btn-navy">
            <Calendar className="w-4 h-4" />
            Schedule Free Consultation
          </a>
          <a
            href="tel:8885291315"
            className="inline-flex items-center gap-2 border-2 border-navy text-navy font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-navy hover:text-gold"
          >
            <Phone className="w-4 h-4" />
            888-529-1315
          </a>
        </div>
      </div>
    </section>
  );
}

function useHead(p: NycPage) {
  useEffect(() => {
    const canonical = `${SITE}${p.url}`;
    if (typeof document !== 'undefined') {
      applyPageMeta({ title: p.metaTitle, description: p.metaDescription, url: canonical, type: 'article' });
    }
  }, [p]);
}

export default function LongFormArticleTemplate({ page }: Props) {
  const schema = buildSchema(page);
  useHead(page);
  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main id="nyc-article">
        <Hero p={page} />
        <Body p={page} />
        <Faq p={page} />
        <SiblingGuides p={page} />
        <RelatedPracticeAreas p={page} />
        <CtaBand p={page} />
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
